// TUT-578 — o RECORTE da entrega: a régua UMA dos painéis de prazo da Visão
// geral ("Prazos de entrega", "Próximos prazos", a Série "Entregas a vencer") e
// das listas que eles abrem (`/agendamento-lista` e as abas Quesitos e Laudos
// de `/elaboracao`). O número do painel é o tamanho da lista filtrada por esta
// função (regra TUT-476): quem conta chama `passaNaEntrega` com o recorte do
// link, e a lista filtra com o mesmo recorte lido da URL.
//
// Só importa módulos sem importação (`faixa-da-entrega`, `periodo-de-datas`,
// `format`): a busca das rotas (`rotas/buscas.ts`) lê este arquivo, e puxar a
// lista do Agendamento daqui fecharia o ciclo de tipos com a rota.
import { FAIXAS_DA_ENTREGA, type FaixaDaEntrega } from "./faixa-da-entrega";
import { calendarDateKey } from "./format";
import { diaDeCalendario, rotuloDoPeriodo } from "./periodo-de-datas";

export { FAIXAS_DA_ENTREGA, type FaixaDaEntrega } from "./faixa-da-entrega";

/** A chave `entrega` da URL: uma faixa, ou `todas` (o que TEM prazo de entrega). */
export const FILTROS_DE_ENTREGA = [...FAIXAS_DA_ENTREGA, "todas"] as const;
export type FiltroDeEntrega = (typeof FILTROS_DE_ENTREGA)[number];

/** O recorte que viaja na URL: a faixa e/ou o período (dias do escritório, inclusivos). */
export interface RecorteDaEntrega {
  entrega?: FiltroDeEntrega | undefined;
  entregaDe?: string | undefined;
  entregaAte?: string | undefined;
}

const UMA_HORA = 3_600_000;

/** A faixa da entrega no instante `agora`, ou `null` (sem prazo, ou além das 72 h). */
export function faixaDaEntrega(entrega: string | null, agora: Date): FaixaDaEntrega | null {
  if (!entrega) return null;
  const falta = new Date(entrega).getTime() - agora.getTime();
  if (Number.isNaN(falta)) return null;
  if (falta < 0) return "vencidos";
  if (falta < 24 * UMA_HORA) return "ate-24h";
  if (falta < 72 * UMA_HORA) return "24-72h";
  return null;
}

export function temRecorteDaEntrega(recorte: RecorteDaEntrega): boolean {
  return Boolean(recorte.entrega || recorte.entregaDe || recorte.entregaAte);
}

/**
 * O prazo de entrega `entrega` (ISO) cai no recorte? Sem recorte, tudo passa;
 * com qualquer chave, o que não tem prazo sai. O período compara o DIA do
 * escritório (`calendarDateKey`), o mesmo dia do balde da Série.
 */
export function passaNaEntrega(entrega: string | null, recorte: RecorteDaEntrega, agora: Date): boolean {
  if (!temRecorteDaEntrega(recorte)) return true;
  if (!entrega) return false;
  if (recorte.entrega && recorte.entrega !== "todas" && faixaDaEntrega(entrega, agora) !== recorte.entrega) {
    return false;
  }
  if (recorte.entregaDe || recorte.entregaAte) {
    const dia = calendarDateKey(entrega);
    if (recorte.entregaDe && dia < recorte.entregaDe) return false;
    if (recorte.entregaAte && dia > recorte.entregaAte) return false;
  }
  return true;
}

/** As entregas por faixa — o número que o painel da Visão geral mostra. */
export type ContagemDaEntrega = Record<FaixaDaEntrega, number>;

/**
 * A contagem de um painel É o tamanho da lista que o link abre: `contar`
 * recebe o recorte do link e devolve quantos itens o filtro DA LISTA mantém.
 * Sem régua própria, o número e a lista não têm como discordar.
 */
export function contagemPorFaixa(contar: (recorte: RecorteDaEntrega) => number): ContagemDaEntrega {
  return Object.fromEntries(
    FAIXAS_DA_ENTREGA.map((faixa) => [faixa, contar({ entrega: faixa })]),
  ) as ContagemDaEntrega;
}

/**
 * O recorte saneado da URL (a rota e a tela chamam esta função): faixa fora da
 * lista cai, dia que não existe cai, e o período invertido cai INTEIRO.
 */
export function recorteDaEntregaDaBusca(busca: {
  entrega?: unknown;
  entregaDe?: unknown;
  entregaAte?: unknown;
}): RecorteDaEntrega {
  const entrega = (FILTROS_DE_ENTREGA as readonly unknown[]).includes(busca.entrega)
    ? (busca.entrega as FiltroDeEntrega)
    : undefined;
  const de = diaDeCalendario(busca.entregaDe);
  const ate = diaDeCalendario(busca.entregaAte);
  const invertido = Boolean(de && ate && de > ate);
  return {
    entrega,
    entregaDe: invertido ? undefined : de,
    entregaAte: invertido ? undefined : ate,
  };
}

const ROTULO_DO_FILTRO: Record<FiltroDeEntrega, string> = {
  vencidos: "Entrega: vencidos",
  "ate-24h": "Entrega: em até 24 h",
  "24-72h": "Entrega: de 24 a 72 h",
  todas: "Com prazo de entrega",
};

/** Os chips do recorte (a chave da URL que o X tira, e o texto do chip). */
export function chipsDaEntrega(
  recorte: RecorteDaEntrega,
): { chave: "entrega" | "entregaPeriodo"; rotulo: string }[] {
  const chips: { chave: "entrega" | "entregaPeriodo"; rotulo: string }[] = [];
  if (recorte.entrega) chips.push({ chave: "entrega", rotulo: ROTULO_DO_FILTRO[recorte.entrega] });
  if (recorte.entregaDe || recorte.entregaAte) {
    chips.push({ chave: "entregaPeriodo", rotulo: `Entrega ${rotuloDoPeriodo(recorte.entregaDe, recorte.entregaAte)}` });
  }
  return chips;
}

/** O que o X do chip tira da URL. */
export const SEM_O_CHIP_DA_ENTREGA = {
  entrega: { entrega: undefined },
  entregaPeriodo: { entregaDe: undefined, entregaAte: undefined },
} as const;
